import { useAppSelector } from '../../hooks/useAppSelector';
import { styled } from '@mui/material/styles';
import {
  Control,
  FieldValues,
  DeepMap,
  FieldError,
  RegisterOptions,
  Controller,
} from 'react-hook-form';
import { Box, FormControl } from '@mui/material';
import * as referenceSlice from '../../data/reference.slice';
import TextInput2 from './TextInput2';
import SelectMenu2 from './SelectMenu2';
import { useTranslation } from 'react-i18next';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import validationUtils from '../../services/validationUtils';

const PREFIX = 'PhoneInput';

const classes = {
  phone: `${PREFIX}-phone`,
  country: `${PREFIX}-country`,
  number: `${PREFIX}-number`,
  extension: `${PREFIX}-extension`,
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  [`& .${classes.phone}`]: {
    display: 'flex',
  },

  [`& .${classes.country}`]: {
    width: '35%',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },

  [`& .${classes.number}`]: {
    flexGrow: 1,
  },

  [`& .${classes.extension}`]: {
    width: 80,
  },
}));

interface PhoneInput2Props {
  label: string;
  name: string;
  className?: string;
  extensionName: string;
  control: Control;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  defaultValue?: any;
}

export default function PhoneInput2({
  label,
  name,
  className,
  extensionName,
  control,
  required = false,
  errors = undefined,
  defaultValue = undefined,
}: PhoneInput2Props) {
  const { t } = useTranslation('common');

  return (
    <StyledFormControl className={className}>
      <Box component="div" className={classes.phone}>
        <Controller
          name={name}
          control={control}
          rules={{
            validate: (value) => {
              const isValid =
                value &&
                matchIsValidTel(value, {
                  onlyCountries: ['US', 'CA', 'PR'],
                });
              const isEmpty = !value || value === '+1';
              let errorMessage = undefined;
              if (required && !isValid) {
                errorMessage = t('common.invalidPhone');
              } else if (!isValid && !isEmpty) {
                errorMessage = t('common.invalidPhone');
              }
              return errorMessage;
            },
          }}
          render={({
            field: { ref: fieldRef, value, ...fieldProps },
            fieldState,
          }) => (
            <MuiTelInput
              {...fieldProps}
              required={required}
              value={value ?? ''}
              inputRef={fieldRef}
              onlyCountries={['US', 'CA', 'PR']}
              helperText={fieldState.invalid ? t('common.invalidPhone') : ''}
              error={fieldState.invalid}
              defaultCountry="US"
              label={label}
              className={classes.number}
              variant="outlined"
            />
          )}
        />
        {extensionName && (
          <TextInput2
            label={t('common.extension')}
            type="number"
            name={extensionName}
            required={false}
            control={control}
            errors={errors}
            defaultValue={defaultValue}
            className={classes.extension}
            inputProps={{ maxLength: 10, min: 0 }}
            rules={{
              pattern: validationUtils.extNumberValidation(),
            }}
          />
        )}
      </Box>
    </StyledFormControl>
  );
}
